import { useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PlanCell } from '../../../types/plan';
import { planCellKey } from '../../../utils/planCells';
import { useEntityInteraction } from '../../../hooks/useEntityInteraction';
import { useCreateBulkBedsWithPlantingsMutation } from '../../../api/bulkOperationsApi';
import { useGetAutomationStatusQuery, useToggleAutomationMutation } from '../../../api/automationApi';
import type { HeatingConfig } from '../../../api/automationApi';
import { useUpdatePlantingMutation } from '../../../api/plantingsApi';
import { useGridScrollMemory } from './useGridScrollMemory';
import { usePlanningSelection } from './usePlanningSelection';
import { useBulkPlacementPreview } from './useBulkPlacementPreview';
import { useEntityPanelData } from './useEntityPanelData';
import { useManagementHandlers } from './useManagementHandlers';
import { useBulkPlacementHandlers } from './useBulkPlacementHandlers';
import type { PlanEditorProps } from './PlanEditor.types';
import { usePlanEditorState } from './usePlanEditorState';
import { usePlanEditorInteractions } from './usePlanEditorInteractions';
import { useGreenhouseGroups } from '../planGrid/useGreenhouseGroups';
import type { GreenhouseOptions } from '../GreenhouseOptionsPanel';

type StructuralObject = NonNullable<PlanEditorProps['structuralObjects']>[number];

export const usePlanEditorController = (props: PlanEditorProps) => {
  const navigate = useNavigate();
  const state = usePlanEditorState(props.initialMode);
  const isPlotViewport = Boolean(props.plotId);
  const [createBulkBedsWithPlantings] = useCreateBulkBedsWithPlantingsMutation();
  const [toggleAutomation] = useToggleAutomationMutation();
  const [updatePlanting] = useUpdatePlantingMutation();
  const automationDate = props.simDate ? props.simDate.slice(0, 10) : undefined;
  const { data: automationStatus } = useGetAutomationStatusQuery(
    { plotId: props.plotId!, saveId: props.saveId! },
    { skip: !props.plotId || !props.saveId },
  );
  const { handleContainerRef, handleScroll } = useGridScrollMemory(props.allowedBoundingBox);
  const cellMap = useMemo(() => {
    const map = new Map<string, PlanCell>();
    props.cells.forEach((cell) => map.set(planCellKey(cell.row, cell.col), cell));
    return map;
  }, [props.cells]);
  const uiCells = useMemo(() => Array.from(cellMap.values()), [cellMap]);
  const [entityState, entityHandlers, entityLookup] = useEntityInteraction(
    uiCells,
    state.appMode === 'management' || state.appMode === 'planning' || state.appMode === 'planting',
  );
  const panelData = useEntityPanelData({
    appMode: state.appMode,
    entityState,
    bedPlantingMap: props.bedPlantingMap,
    multiSelectedBeds: state.multiSelectedBeds,
  });
  const { selectionRect, analyzePlanningSelection } = usePlanningSelection({
    selectionStart: state.selectionStart,
    selectionEnd: state.selectionEnd,
    cellMap,
    globalGrid: props.globalGrid,
    entityLookup,
    bedsWithPlantings: panelData.bedsWithPlantings,
  });

  const bulkPlacementPreview = useBulkPlacementPreview({
    isBulkPlacementMode: state.isBulkPlacementMode,
    bulkGridDimensions: state.bulkGridDimensions,
    bulkPlacementHoverPos: state.bulkPlacementHoverPos,
    cellMap,
    allowedBoundingBox: props.allowedBoundingBox,
    boundingBox: props.boundingBox,
    globalGrid: props.globalGrid,
  });
  const managementHandlers = useManagementHandlers({
    navigate,
    saveId: props.saveId,
    setSelectedBedForPlanting: state.setSelectedBedForPlanting,
    setPlantingModalVisible: state.setPlantingModalVisible,
    clearEntitySelection: entityHandlers.clearSelection,
    clearMultiSelectedBeds: () => state.setMultiSelectedBeds(new Set()),
    onManagementViewBed: props.onManagementViewBed,
    onManagementViewPlanting: props.onManagementViewPlanting,
    onManagementAddPlanting: props.onManagementAddPlanting,
    onManagementBedAction: props.onManagementBedAction,
    onManagementPlantingAction: props.onManagementPlantingAction,
  });
  const bulkPlacementHandlers = useBulkPlacementHandlers({
    plotId: props.plotId,
    fieldId: props.fieldId,
    saveId: props.saveId,
    farmId: props.farmId,
    selectedCropForBulk: state.selectedCropForBulk,
    bulkPlacementPreview,
    createBulkBedsWithPlantings,
    onPatch: props.onPatch,
    setBulkPlacementModalVisible: state.setBulkPlacementModalVisible,
    setSelectedCropForBulk: state.setSelectedCropForBulk,
    setBulkGridDimensions: state.setBulkGridDimensions,
    setIsBulkPlacementMode: state.setIsBulkPlacementMode,
    setBulkPlacementHoverPos: state.setBulkPlacementHoverPos,
  });

  const interactions = usePlanEditorInteractions({
    props,
    appMode: state.appMode,
    isDragging: state.isDragging,
    isEraseMode: state.isEraseMode,
    isBulkPlacementMode: state.isBulkPlacementMode,
    planningSelection: state.planningSelection,
    selectionStart: state.selectionStart,
    selectionEnd: state.selectionEnd,
    selectionRect,
    bulkPlacementPreview,
    entityLookup,
    entityState,
    entityHandlers,
    analyzePlanningSelection,
    handleConfirmBulkPlacement: bulkPlacementHandlers.handleConfirmBulkPlacement,
    state,
  });

  // ─── Greenhouse info for selected entity ──────────────────────────────
  const structuralObjects = useMemo(() => props.structuralObjects ?? [], [props.structuralObjects]);
  const greenhouseGroups = useGreenhouseGroups(cellMap, structuralObjects);

  // Build SO lookup for parameters
  const soLookup = useMemo(() => {
    const m = new Map<string, StructuralObject>();
    for (const so of structuralObjects) {
      m.set(so.id, so);
    }
    return m;
  }, [structuralObjects]);

  // Build a map: cellKey → greenhouse group
  const cellToGreenhouseGroup = useMemo(() => {
    const m = new Map<string, typeof greenhouseGroups[0]>();
    for (const g of greenhouseGroups) {
      g.cellKeys.forEach((key) => m.set(key, g));
    }
    return m;
  }, [greenhouseGroups]);

  // Tracks greenhouse group selected by clicking directly on a greenhouse cell (incl. empty stubs)
  const [manualGreenhouseGroupId, setManualGreenhouseGroupId] = useState<string | null>(null);

  // Wrap handleMouseDown to detect greenhouse cell clicks in management mode
  const handleMouseDown = useCallback((row: number, col: number) => {
    if (state.appMode === 'management') {
      const key = planCellKey(row, col);
      const group = cellToGreenhouseGroup.get(key);
      setManualGreenhouseGroupId(group ? group.id : null);
    }
    interactions.handleMouseDown(row, col);
  }, [state.appMode, cellToGreenhouseGroup, interactions]);

  // Determine which greenhouse group is selected (if any cell in selection is in a group)
  const selectedGreenhouseInfo = useMemo(() => {
    if (state.appMode !== 'management') return null;

    const buildInfo = (group: typeof greenhouseGroups[0]) => {
      const so = soLookup.get(group.id);
      const soParams = so?.parameters ?? {};

      return {
        groupId: group.id,
        subtype: group.subtype,
        cellCount: group.cellKeys.size,
        cellKeys: group.cellKeys,
        soParameters: soParams,
        activeFrom: so?.activeFrom ?? null,
        activeTo: so?.activeTo ?? null,
        options: {
          autoVentilation: soParams.autoVentilation === true || soParams.ventilationEnabled === true,
          ventilationEnabled: soParams.autoVentilation === true || soParams.ventilationEnabled === true,
          autoHeating: false,
          ventilationMaxC: soParams.ventilationMaxC as number | null | undefined,
        } as GreenhouseOptions,
      };
    };

    // Check selected entity cells (bed clicked inside greenhouse)
    const keys = Array.from(entityState.selectedCellKeys);
    for (let i = 0; i < keys.length; i++) {
      const group = cellToGreenhouseGroup.get(keys[i]);
      if (group) return buildInfo(group);
    }

    // Fallback: user clicked directly on a greenhouse empty cell
    if (manualGreenhouseGroupId) {
      const group = greenhouseGroups.find((g) => g.id === manualGreenhouseGroupId);
      if (group) return buildInfo(group);
    }

    return null;
  }, [state.appMode, entityState.selectedCellKeys, cellToGreenhouseGroup, soLookup, manualGreenhouseGroupId, greenhouseGroups]);

  const handleGreenhouseDelete = useCallback(() => {
    if (!selectedGreenhouseInfo) return;
    props.onPatch([{ op: 'deleteStructuralObject', structuralObjectId: selectedGreenhouseInfo.groupId }]);
  }, [selectedGreenhouseInfo, props]);

  const handleGreenhouseParamChange = useCallback((params: Partial<GreenhouseOptions>) => {
    if (!selectedGreenhouseInfo) return;
    const so = soLookup.get(selectedGreenhouseInfo.groupId);
    const currentParams = so?.parameters ?? {};
    props.onPatch([{
      op: 'updateStructuralObject',
      structuralObjectId: selectedGreenhouseInfo.groupId,
      parameters: { ...currentParams, ...params },
    }]);
  }, [selectedGreenhouseInfo, soLookup, props]);

  // Install/removal window: from which day (and until when) the covering affects beds.
  const handleGreenhouseActiveWindowChange = useCallback((activeFrom: string | null, activeTo: string | null) => {
    if (!selectedGreenhouseInfo) return;
    props.onPatch([{
      op: 'updateStructuralObject',
      structuralObjectId: selectedGreenhouseInfo.groupId,
      activeFrom,
      activeTo,
    }]);
  }, [selectedGreenhouseInfo, props]);

  // ─── Auto-harvest for plantings (planting-level parameter) ─────────
  const handlePlantingAutoHarvestToggle = useCallback((plantingId: string, value: boolean) => {
    if (!props.saveId || !plantingId) return;
    updatePlanting({
      saveId: props.saveId,
      id: plantingId,
      payload: { autoHarvest: value },
    });
  }, [props.saveId, updatePlanting]);

  // ─── Automation status (watering / lighting / heating) ────────────
  const selectedBedAutoWatering = useMemo(() => {
    if (state.appMode !== 'management' || !automationStatus) return undefined;
    const bedInfo = panelData.selectedBedInfo;
    if (!bedInfo) return undefined;
    return automationStatus.watering[bedInfo.bedId]?.enabled ?? false;
  }, [state.appMode, panelData.selectedBedInfo, automationStatus]);

  const selectedBedLighting = useMemo(() => {
    if (state.appMode !== 'management' || !automationStatus) return undefined;
    const bedInfo = panelData.selectedBedInfo;
    if (!bedInfo) return undefined;
    return automationStatus.lighting[bedInfo.bedId]?.enabled ?? false;
  }, [state.appMode, panelData.selectedBedInfo, automationStatus]);

  const selectedGreenhouseHeating = useMemo(() => {
    if (!selectedGreenhouseInfo || !automationStatus) return { enabled: false, config: null as HeatingConfig | null };
    const soState = automationStatus.heating[selectedGreenhouseInfo.groupId];
    return {
      enabled: soState?.enabled ?? false,
      config: (soState?.properties as HeatingConfig) ?? null,
    };
  }, [selectedGreenhouseInfo, automationStatus]);

  const handleBedAutoWateringToggle = useCallback((value: boolean) => {
    const bedInfo = panelData.selectedBedInfo;
    if (!bedInfo || !props.saveId) return;
    toggleAutomation({
      saveId: props.saveId,
      automationType: 'watering',
      enabled: value,
      targetId: bedInfo.bedId,
      date: automationDate,
    });
  }, [panelData.selectedBedInfo, toggleAutomation, props.saveId, automationDate]);

  const handleBedLightingToggle = useCallback((value: boolean) => {
    const bedInfo = panelData.selectedBedInfo;
    if (!bedInfo || !props.saveId) return;
    toggleAutomation({
      saveId: props.saveId,
      automationType: 'lighting',
      enabled: value,
      targetId: bedInfo.bedId,
      date: automationDate,
    });
  }, [panelData.selectedBedInfo, toggleAutomation, props.saveId, automationDate]);

  const handleHeatingToggle = useCallback((enabled: boolean) => {
    if (!selectedGreenhouseInfo || !props.saveId) return;
    toggleAutomation({
      saveId: props.saveId,
      automationType: 'heating',
      enabled,
      targetId: selectedGreenhouseInfo.groupId,
      properties: enabled ? { minC: 5, maxC: 25, ventilationMaxC: 35 } : undefined,
      date: automationDate,
    });
  }, [selectedGreenhouseInfo, toggleAutomation, props.saveId, automationDate]);

  const handleHeatingConfigChange = useCallback((config: HeatingConfig) => {
    if (!selectedGreenhouseInfo || !props.saveId) return;
    toggleAutomation({
      saveId: props.saveId,
      automationType: 'heating',
      enabled: true,
      targetId: selectedGreenhouseInfo.groupId,
      properties: config,
      date: automationDate,
    });
  }, [selectedGreenhouseInfo, toggleAutomation, props.saveId, automationDate]);

  const isSelectedCell = (row: number, col: number) => Boolean(selectionRect) && row !== 0 && col !== 0 && row >= selectionRect!.minRow && row <= selectionRect!.maxRow && col >= selectionRect!.minCol && col <= selectionRect!.maxCol;

  return {
    appMode: state.appMode,
    isPlotViewport,
    isEraseMode: state.isEraseMode,
    isBulkPlacementMode: state.isBulkPlacementMode,
    bulkGridDimensions: state.bulkGridDimensions,
    multiSelectedBeds: state.multiSelectedBeds,
    multiSelectedPlots: state.multiSelectedPlots,
    selectionRect,
    planningSelection: state.planningSelection,
    entityState,
    entityLookup,
    cellMap,
    selectedBedForPlanting: state.selectedBedForPlanting,
    plantingModalVisible: state.plantingModalVisible,
    bulkPlacementModalVisible: state.bulkPlacementModalVisible,
    handleContainerRef,
    handleScroll,
    handleMouseDown: handleMouseDown,
    handleMouseEnter: interactions.handleMouseEnter,
    handleMouseLeave: interactions.handleMouseLeave,
    handleMouseUp: interactions.handleMouseUp,
    handleGridMouseLeave: interactions.handleGridMouseLeave,
    handlePlanningAction: interactions.handlePlanningAction,
    handleAppModeChange: interactions.handleAppModeChange,
    setIsEraseMode: state.setIsEraseMode,
    setPlanningSelection: state.setPlanningSelection,
    panelData,
    managementHandlers,
    bulkPlacementHandlers,
    selectedGreenhouseInfo,
    handleGreenhouseParamChange,
    handleGreenhouseDelete,
    handleGreenhouseActiveWindowChange,
    handlePlantingAutoHarvestToggle,
    selectedBedAutoWatering,
    selectedBedLighting,
    selectedGreenhouseHeating,
    handleBedAutoWateringToggle,
    handleBedLightingToggle,
    handleHeatingToggle,
    handleHeatingConfigChange,
    workspace: { isSelectedCell, bulkPlacementPreview, bedsWithPlantings: panelData.bedsWithPlantings },
    closePlantingModal: state.closePlantingModal,
    closeBulkPlacementModal: () => state.setBulkPlacementModalVisible(false),
  };
};
